import { Dashboard } from '../../models/Dashboard'
import { RecordFactory } from '../../models/RecordFactory'
import { Subscription } from '../../models/Subscription'
import { getViewTitleFromView } from '../../utils/dashboard'
import { ComponentData } from '../../utils/typing'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import Paragraph from '../app/design/Paragraph'
import {
    ClickableTableRow,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../app/design/Table'
import { DashboardSkeleton } from '../common/skeletons/DashboardSkeleton'
import { ClientTableCell } from './ClientTableCell'
import { EmptyDashboardLayout } from './EmptyDashboardLayout'
import { KeyMetric } from './KeyMetric'

type Props = {
    subscriptions: Subscription[]
    view: number
    dashboard?: Dashboard
    searchText: string
    isLoading: boolean
}

const viewWillAppear = async (base: PocketComponent<Props>) => {
    return await Promise.all(
        base.props.subscriptions.map((s) => RecordFactory.getInstance().loadSubscription(s.id!))
    )
}

const render = ({ base }: PocketComponentProps<ComponentData<typeof viewWillAppear>, Props>) => {
    const { subscriptions, view, dashboard, searchText, isLoading } = base.props

    if (isLoading) {
        return <DashboardSkeleton />
    }

    return (
        <div className="flex flex-col gap-6">
            <KeyMetric
                metrics={[
                    {
                        title: `${getViewTitleFromView(dashboard, view)} ${
                            dashboard?.value === 1 ? `subscription` : `subscriptions`
                        }`,
                        value: (dashboard?.value ?? 0).toString(),
                    },
                ]}
            />
            <Table>
                <TableHeader>
                    <TableRow location="head">
                        <TableHead className="font-normal">Client</TableHead>
                        <TableHead className="font-normal">Plan</TableHead>
                        <TableHead className="font-normal">
                            {view === 6
                                ? 'Cancelled date'
                                : view === 4
                                  ? 'Completed date'
                                  : 'Next invoice date'}
                        </TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {subscriptions.map((s) => (
                        <ClickableTableRow key={s.id} href={`/subscriptions/${s.id}`}>
                            <TableCell>
                                <ClientTableCell user={s.client} subaccount={s.subaccount} />
                            </TableCell>
                            <TableCell>
                                <Paragraph size="sm">{s.plan?.name}</Paragraph>
                            </TableCell>

                            <TableCell>
                                <Paragraph size="sm">
                                    {view === 6
                                        ? s.cancelledDate?.format('MMM D, YYYY')
                                        : view === 4
                                          ? s.completedDate?.format('MMM D, YYYY')
                                          : s.nextInvoiceDate?.format('MMM D, YYYY')}
                                </Paragraph>
                            </TableCell>
                        </ClickableTableRow>
                    ))}
                    {subscriptions.length === 0 && (
                        <TableCell colSpan={10}>
                            <EmptyDashboardLayout dashboard={dashboard} searchtext={searchText} />
                        </TableCell>
                    )}
                </TableBody>
            </Table>
        </div>
    )
}

export const SubscriptionsDashboard = usePSComponent(render, viewWillAppear)
